<template>
  <BaseModal
    ref="modal"
    class="dev-modal-instance"
    title="Dev tools"
    :can-click-outside="true"
    :width="520"
  >
    <template #header-actions>
      <BaseButton
        variant="ghost"
        size="small"
        text="Reset"
        title="Clears localStorage, sessionStorage, and cookies except autofill defaults, then reloads"
        data-qa="feature-flag-reset-button"
        @click="handleReset"
      >
        <template #prepend>
          <RestoreIcon />
        </template>
      </BaseButton>
      <BaseButton
        variant="ghost"
        size="small"
        text="Reload"
        data-qa="feature-flag-reload-button"
        @click="handleReload"
      >
        <template #prepend>
          <RefreshIcon />
        </template>
      </BaseButton>
    </template>
    <div class="dev-modal">
      <section
        v-if="userGuid || userEmail"
        class="dev-modal__section"
      >
        <h2 class="dev-modal__section-title">User</h2>
        <div
          v-if="userGuid"
          class="dev-modal__row"
        >
          <div class="dev-modal__row-body">
            <span class="dev-modal__row-name">GUID</span>
            <span class="dev-modal__row-description">
              {{ userGuid }}
            </span>
          </div>
          <BaseButton
            variant="ghost"
            icon
            size="small"
            title="Copy GUID"
            data-qa="feature-flag-copy-guid-button"
            @click="handleCopyGuid"
          >
            <CopyIcon />
          </BaseButton>
        </div>
        <div
          v-if="userEmail"
          class="dev-modal__row"
        >
          <div class="dev-modal__row-body">
            <span class="dev-modal__row-name">Email</span>
            <span class="dev-modal__row-description">
              {{ userEmail }}
            </span>
          </div>
          <BaseButton
            variant="ghost"
            icon
            size="small"
            title="Copy email"
            data-qa="feature-flag-copy-email-button"
            @click="handleCopyEmail"
          >
            <CopyIcon />
          </BaseButton>
        </div>
      </section>

      <section class="dev-modal__section">
        <h2 class="dev-modal__section-title">General settings</h2>
        <div class="dev-modal__row">
          <div class="dev-modal__row-body">
            <span class="dev-modal__row-name">from_app</span>
            <span class="dev-modal__row-description"
              >Simulate app-to-web handoff</span
            >
          </div>
          <ToggleField
            :model-value="fromAppEnabled"
            data-qa="feature-flag-toggle-from-app"
            @update:model-value="handleFromAppToggle"
          />
        </div>
        <div class="dev-modal__row">
          <div class="dev-modal__row-body">
            <span class="dev-modal__row-name">Autofill</span>
            <span class="dev-modal__row-description"
              >Show autofill button on sign-up pages</span
            >
          </div>
          <ToggleField
            :model-value="autofillStore.enabled"
            data-qa="feature-flag-toggle-autofill"
            @update:model-value="autofillStore.enabled = $event"
          />
        </div>
      </section>

      <section class="dev-modal__section">
        <h2 class="dev-modal__section-title">Feature flags</h2>
        <p class="dev-modal__section-hint">
          Override with query params like
          <code>?ff_webOptimizedFunnel=true|false|default</code>
        </p>
        <div
          v-for="key in featureFlagStore.flagKeys"
          :key="key"
          class="dev-modal__row"
        >
          <div class="dev-modal__row-body">
            <span class="dev-modal__row-name">{{ key }}</span>
            <span class="dev-modal__row-description">
              Default:
              {{ formatFlag(featureFlagStore.flagsByKey[key].defaultEnabled) }}
              &middot; Remote:
              {{ formatFlag(featureFlagStore.flagsByKey[key].remoteEnabled) }}
            </span>
          </div>
          <template v-if="getPreSignupExperiment(key)">
            <SelectField
              label="Variant"
              :model-value="getActiveVariantKey(key) ?? ''"
              :options="getVariantOptions(key)"
              :data-qa="`feature-flag-variant-select-${key}`"
              @update:model-value="
                featureFlagStore.setPreSignupVariantOverride(
                  key,
                  $event || null,
                )
              "
            />
          </template>
          <template v-else>
            <BaseButton
              v-if="featureFlagStore.flagsByKey[key].localEnabled !== null"
              variant="ghost"
              icon
              size="small"
              title="Reset to default"
              :data-qa="`feature-flag-reset-${key}`"
              @click="featureFlagStore.setOverride(key, null)"
            >
              <RestoreIcon />
            </BaseButton>
            <ToggleField
              :model-value="featureFlagStore.flags[key]"
              :class="{
                'toggle--following-default':
                  featureFlagStore.flagsByKey[key].localEnabled === null,
              }"
              :data-qa="`feature-flag-toggle-${key}`"
              @update:model-value="featureFlagStore.setOverride(key, $event)"
            />
          </template>
        </div>
      </section>

      <section class="dev-modal__section">
        <h2 class="dev-modal__section-title">Autofill defaults</h2>
        <div class="dev-modal__autofill-fields">
          <div class="dev-modal__name-row">
            <TextField
              v-model="autofillStore.fields.firstName"
              label="First name"
            />
            <TextField
              v-model="autofillStore.fields.lastName"
              label="Last name"
            />
          </div>
          <TextField
            v-model="autofillStore.fields.email"
            hint="Gets a random +suffix on each fill unless you include a + yourself."
            label="Email"
            type="email"
          />
          <PasswordField v-model="autofillStore.fields.password" />
          <PhonenumberField
            v-model="autofillStore.fields.phone"
            country="DE"
            label="Phone"
          />
          <DateOfBirthField v-model="autofillStore.fields.dob" />
        </div>
      </section>
    </div>
  </BaseModal>
</template>

<script setup>
import { computed, ref } from 'vue'
import BaseButton from '@shared/components/BaseButton.vue'
import BaseModal from '@shared/components/BaseModal.vue'
import CopyIcon from '@shared/assets/icon/CopyIcon.vue'
import RefreshIcon from '@shared/assets/icon/RefreshIcon.vue'
import RestoreIcon from '@shared/assets/icon/RestoreIcon.vue'
import DateOfBirthField from '@shared/components/form/DateOfBirthField.vue'
import PasswordField from '@shared/components/form/PasswordField.vue'
import PhonenumberField from '@shared/components/form/PhonenumberField.vue'
import TextField from '@shared/components/form/TextField.vue'
import ToggleField from '@shared/components/form/ToggleField.vue'
import SelectField from '@shared/components/form/SelectField.vue'
import { isFromApp, setFromApp } from '@shared/helpers/isFromApp'
import { localStorage, sessionStorage } from '@shared/helpers/storage'
import useAuthStore from '@shared/stores/useAuthStore'
import useFeatureFlagStore, {
  PRE_SIGNUP_EXPERIMENTS,
} from '@shared/stores/useFeatureFlagStore'
import useAutofillStore from '@shared/stores/useAutofillStore'
import useUserStore from '@shared/stores/useUserStore'

const modal = ref(null)
const authStore = useAuthStore()
const featureFlagStore = useFeatureFlagStore()
const autofillStore = useAutofillStore()
const userStore = useUserStore()
const fromAppEnabled = ref(isFromApp())
const userGuid = computed(() => authStore.guid || userStore.user?.guid || '')

const props = defineProps({
  userEmail: {
    type: String,
    default: '',
  },
})

const clearCookie = (cookie) => {
  const trimmedCookie = cookie.trim()
  if (!trimmedCookie) return

  const separatorIndex = trimmedCookie.indexOf('=')
  const key = (
    separatorIndex === -1
      ? trimmedCookie
      : trimmedCookie.slice(0, separatorIndex)
  ).trim()
  if (!key) return

  document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}

const handleReset = () => {
  const autofillState = localStorage.getItem('autofill')
  localStorage.clear()
  if (autofillState) {
    localStorage.setItem('autofill', autofillState)
  }
  sessionStorage.clear()
  document.cookie.split(';').forEach(clearCookie)
  window.location.reload()
}

const formatFlag = (value) => (typeof value === 'boolean' ? String(value) : '–')

const getPreSignupExperiment = (key) =>
  PRE_SIGNUP_EXPERIMENTS.find((e) => e.featureFlagKey === key) ?? null

const getVariantOptions = (key) => {
  const experiment = getPreSignupExperiment(key)
  if (!experiment) return []
  return [
    { text: 'random', attrs: { value: 'random' } },
    ...experiment.variants.map((v) => ({
      text: v.variantKey,
      attrs: { value: v.variantKey },
    })),
  ]
}

const getActiveVariantKey = (key) => {
  const assignment = featureFlagStore.preSignupAssignments.find(
    (a) => a.featureFlagKey === key,
  )
  return assignment?.variantKey ?? null
}

const handleFromAppToggle = (enabled) => {
  fromAppEnabled.value = enabled
  setFromApp(enabled)
}

const handleReload = () => {
  window.location.reload()
}

const handleCopyGuid = async () => {
  await navigator.clipboard?.writeText(userGuid.value)
}

const handleCopyEmail = async () => {
  await navigator.clipboard?.writeText(props.userEmail)
}

const open = () => {
  fromAppEnabled.value = isFromApp()
  modal.value?.open()
}

defineExpose({ open })
</script>

<style lang="scss">
.dev-modal-instance .modal-mask__container {
  outline: 4px solid $kds-color-background-yellow-accent;
}
</style>

<style lang="scss" scoped>
.dev-modal {
  display: flex;
  flex-direction: column;
  gap: $kds-space-2x;

  &__section-title {
    @include kds-typography-title-medium;
    margin: 0 0 $kds-space-1x;
  }

  &__section-hint {
    @include kds-typography-paragraph-small;
    color: $kds-color-text-secondary;
    margin: 0 0 $kds-space-1x;
  }

  &__name-row {
    display: flex;
    gap: $kds-space-1x;

    > * {
      flex: 1;
    }
  }

  &__autofill-fields {
    display: flex;
    flex-direction: column;
    gap: $kds-space-1x;
  }

  &__row {
    align-items: center;
    border-top: 1px solid $kds-color-line-subtle;
    display: flex;
    gap: $kds-space-2x;
    justify-content: space-between;
    padding: $kds-space-1x 0;
  }

  .toggle-container:has(.toggle--following-default) {
    opacity: 0.6;
  }

  &__row-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__row-name {
    @include kds-typography-ui-strong-medium;
  }

  &__row-description {
    @include kds-typography-paragraph-small;
    color: $kds-color-text-secondary;
  }
}
</style>
